import React from 'react';
import { Box, Card, Text, Title } from '@mantine/core';
import { Navigate, useLocation } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { canAdministerCustomer } from '@/lib/permissions';
import { returnTo } from './returnTo';

/**
 * Gate for surfaces a CUSTOMER ADMIN may use, not just Nodus staff.
 *
 * Admits superusers, staff, and any user with ``CustomerAccess.admin`` on the
 * currently active customer. The distinction from ``SuperAdminGuard`` is who
 * the page is *for*, not how sensitive it is: these pages read data the
 * backend already scopes to the active customer with ``@CustomerRequired()``,
 * so a customer admin sees their own site and nothing else.
 *
 * Diagnostic sessions were the case that prompted this. Their endpoints have
 * always been ``@CustomerRequired()`` rather than superuser-gated — the API
 * was open to customer admins the whole time and only the UI hid it.
 *
 * This guard is therefore NOT a security boundary and must not be treated as
 * one; the server-side scoping is. It exists so the menu and the page agree
 * about who the surface is for.
 */
export const CustomerAdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, activeCustomer } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: returnTo(location) }} />;
  }

  if (!canAdministerCustomer(user, activeCustomer)) {
    return (
      <Box mih="100vh" display="flex" style={{alignItems:'center',justifyContent:'center',backgroundColor:'var(--mantine-color-body)'}}>
        <Card withBorder padding="lg" w="100%" maw={448}>
          <Box ta="center" p="1rem">
            <Box ml="auto" mr="auto" mb={16} h={48} w={48} display="flex" style={{alignItems:'center',justifyContent:'center',borderRadius:9999,backgroundColor:'var(--mantine-color-red-1)'}}>
              <Shield style={{height:24,width:24,color:'var(--mantine-color-red-6)'}} />
            </Box>
            <Title order={4} fz="xl">Access Denied</Title>
            <Text size="sm" c="dimmed">
              This page is available to administrators of this customer.
            </Text>
          </Box>
          <Box ta="center" p="0 1rem 1rem">
            <Text component="p" fz="sm" c="dimmed">
              Please contact your administrator if you believe you should have access to this page.
            </Text>
          </Box>
        </Card>
      </Box>
    );
  }

  return <>{children}</>;
};
